import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { inmuebleService } from '../services/inmuebleService';
import { operacionTicketService } from '../services/operacionTicketService';
import { Inmueble } from '../types/Inmueble';
import InmuebleForm from '../components/inmuebles/InmuebleForm';
import GenerarTicketsModal from '../components/inmuebles/GenerarTicketsModal';
import ConfirmModal from '../components/common/ConfirmModal';
import ActionButton from '../components/common/ActionButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { FaEdit, FaTrash, FaTicketAlt, FaEye } from 'react-icons/fa';
import { BsBuildings } from 'react-icons/bs';
import { formatCurrency as formatCurrencyBase } from '../utils/format';
import './Inmuebles.css';

const formatCurrency = (value?: number | string) => formatCurrencyBase(value, 0);

const getEstadoBadgeClass = (estado: string | undefined): string => {
  switch (estado?.toLowerCase()) {
    case 'prospecto': return 'status-badge status-prospecto';
    case 'estudio': return 'status-badge status-estudio';
    case 'negociación': return 'status-badge status-negociacion';
    case 'activo': return 'status-badge status-activo';
    default: return 'status-badge';
  }
};

const getOcupacionBadgeClass = (estado: string | undefined): string => {
  if (!estado) return 'ocupacion-badge ocupacion-vacio';
  if (estado === 'Vacío') return 'ocupacion-badge ocupacion-vacio';
  if (estado.startsWith('Alquilado')) return 'ocupacion-badge ocupacion-alquilado';
  if (estado === 'Ocupado') return 'ocupacion-badge ocupacion-ocupado';
  return 'ocupacion-badge';
};

export interface CreateActionInfo {
  action: () => void;
  label: string;
  visible: boolean;
}

interface InmueblesProps {
  onRegisterAction?: (info: CreateActionInfo) => void;
}

const Inmuebles: React.FC<InmueblesProps> = ({ onRegisterAction }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [inmuebles, setInmuebles] = useState<Inmueble[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingInmueble, setEditingInmueble] = useState<Inmueble | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; label: string } | null>(null);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [selectedInmueble, setSelectedInmueble] = useState<Inmueble | null>(null);
  const [conOperaciones, setConOperaciones] = useState<Set<string>>(new Set());

  // Filtros
  const [filterProyecto, setFilterProyecto] = useState('');
  const [filterPoblacion, setFilterPoblacion] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterClase, setFilterClase] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await inmuebleService.getAll();
      const lista = response.data;
      setInmuebles(lista);
      // Comprobar qué inmuebles activos ya tienen operaciones
      const activos = lista.filter(i => i.estado === 'Activo' && i.id);
      const resultados = await Promise.all(
        activos.map(i =>
          operacionTicketService.getOperacionesByInmueble(i.id!)
            .then(r => ({ id: i.id!, tiene: r.data.length > 0 }))
            .catch(() => ({ id: i.id!, tiene: false }))
        )
      );
      setConOperaciones(new Set(resultados.filter(r => r.tiene).map(r => r.id)));
    } catch (error) {
      console.error('Error cargando inmuebles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingInmueble(null);
    setShowForm(true);
  };

  useEffect(() => {
    onRegisterAction?.({ action: handleCreate, label: 'Crear Inmueble', visible: !showForm });
  }, [showForm, onRegisterAction]);

  const handleEdit = (inmueble: Inmueble) => {
    setEditingInmueble(inmueble);
    setShowForm(true);
  };

  const handleDeleteClick = (id: string, label: string) => {
    setItemToDelete({ id, label });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await inmuebleService.delete(itemToDelete.id);
      showToast('Inmueble eliminado correctamente');
      loadData();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error eliminando inmueble:', error);
      showToast('Error al eliminar el inmueble', 'error');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingInmueble(null);
    loadData();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingInmueble(null);
  };

  const clearFilters = () => {
    setFilterProyecto('');
    setFilterPoblacion('');
    setFilterEstado('');
    setFilterClase('');
  };

  const hasFilters = filterProyecto || filterPoblacion || filterEstado || filterClase;

  const filteredData = inmuebles.filter((i) => {
    const matchProyecto = !filterProyecto ||
      (i.proyectoNombre || '').toLowerCase().includes(filterProyecto.toLowerCase()) ||
      (i.idProyecto || '').toLowerCase().includes(filterProyecto.toLowerCase());
    const matchPoblacion = !filterPoblacion ||
      (i.poblacion || '').toLowerCase().includes(filterPoblacion.toLowerCase());
    const matchEstado = !filterEstado || i.estado === filterEstado;
    const matchClase = !filterClase || i.clase === filterClase;
    return matchProyecto && matchPoblacion && matchEstado && matchClase;
  });

  const renderTable = () => {
    if (loading) return <LoadingSpinner />;
    if (filteredData.length === 0) return <EmptyState icon={BsBuildings} title="No hay inmuebles registrados" subtitle="Añade el primero con el botón + Nuevo inmueble" />;

    return (
      <table className="inmuebles-table">
        <thead>
          <tr>
            <th>Proyecto</th>
            <th>Dirección / Población</th>
            <th>Clase</th>
            <th>Estado</th>
            <th>Ocupación</th>
            <th className="td-right">Coste inversión</th>
            <th className="td-right">Val. mercado</th>
            <th className="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((inmueble) => (
            <tr key={inmueble.id}>
              <td>
                {inmueble.idProyecto ? (
                  <span title={inmueble.idProyecto}>
                    {inmueble.proyectoNombre || inmueble.idProyecto}
                  </span>
                ) : '-'}
              </td>
              <td>
                <div>{inmueble.direccion || '-'}</div>
                {inmueble.poblacion && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {inmueble.cp ? `${inmueble.cp} ` : ''}{inmueble.poblacion}
                  </div>
                )}
              </td>
              <td>{inmueble.clase || '-'}</td>
              <td>
                {inmueble.estado ? (
                  <span className={getEstadoBadgeClass(inmueble.estado)}>{inmueble.estado}</span>
                ) : '-'}
              </td>
              <td>
                {inmueble.estadoOcupacion ? (
                  <span className={getOcupacionBadgeClass(inmueble.estadoOcupacion)}>
                    {inmueble.estadoOcupacion}
                  </span>
                ) : '-'}
              </td>
              <td className="td-right">{formatCurrency(inmueble.costeInversion)}</td>
              <td className="td-right">{formatCurrency(inmueble.valorMercadoActual)}</td>
              <td className="actions-cell">
                <div className="actions">
                  {inmueble.estado === 'Activo' && (
                    conOperaciones.has(inmueble.id!) ? (
                      <ActionButton
                        icon={FaEye}
                        text="Ver tickets"
                        onClick={() => navigate(`/seguimiento-cra/inmuebles/${inmueble.id}/tickets`)}
                        variant="primary"
                      />
                    ) : (
                      <ActionButton
                        icon={FaTicketAlt}
                        text="Generar tickets"
                        onClick={() => { setSelectedInmueble(inmueble); setShowTicketsModal(true); }}
                        variant="primary"
                      />
                    )
                  )}
                  <ActionButton icon={FaEdit} text="Editar Inmueble" onClick={() => handleEdit(inmueble)} variant="edit" />
                  <ActionButton
                    icon={FaTrash}
                    text="Eliminar Inmueble"
                    onClick={() => handleDeleteClick(
                      inmueble.id!,
                      inmueble.direccion || inmueble.idProyecto || 'este inmueble'
                    )}
                    variant="delete"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="inmuebles-page">
      <GenerarTicketsModal
        isOpen={showTicketsModal}
        inmueble={selectedInmueble}
        onSuccess={() => {
          if (selectedInmueble?.id) {
            setConOperaciones(prev => new Set(prev).add(selectedInmueble.id!));
          }
          setShowTicketsModal(false);
          setSelectedInmueble(null);
        }}
        onClose={() => { setShowTicketsModal(false); setSelectedInmueble(null); }}
      />
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar eliminación"
        message={`¿Estás seguro de que deseas eliminar "${itemToDelete?.label}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setShowDeleteModal(false); setItemToDelete(null); }}
        type="danger"
      />


      {!showForm && (
        <div className="filters-container">
          <div className="filters">
            <div className="filter-group">
              <input
                type="text"
                className="filter-input"
                placeholder="Buscar por proyecto..."
                value={filterProyecto}
                onChange={(e) => setFilterProyecto(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <input
                type="text"
                className="filter-input"
                placeholder="Buscar por población..."
                value={filterPoblacion}
                onChange={(e) => setFilterPoblacion(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <select
                className="filter-select"
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
              >
                <option value="">Todos los estados</option>
                <option value="Prospecto">Prospecto</option>
                <option value="Estudio">Estudio</option>
                <option value="Negociación">Negociación</option>
                <option value="Activo">Activo</option>
              </select>
            </div>
            <div className="filter-group">
              <select
                className="filter-select"
                value={filterClase}
                onChange={(e) => setFilterClase(e.target.value)}
              >
                <option value="">Todas las clases</option>
                <option value="Urbano">Urbano</option>
                <option value="Comercial">Comercial</option>
                <option value="Almacenamiento">Almacenamiento</option>
                <option value="Párking">Párking</option>
              </select>
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="btn-clear-filters">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}

      <div className="inmuebles-content">
        {!showForm ? (
          renderTable()
        ) : (
          <div className="form-container">
            <InmuebleForm
              inmueble={editingInmueble}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Inmuebles;
